/**
 * Personal PDF render quality for GA Dashboards (localStorage).
 * Caps how densely pdf.js rasters a page — higher = sharper zoom, more RAM/GPU.
 *
 * Zoom is progressive (like list-card thumbs): a cheap preview paints first,
 * then a sharper layer swaps in when ready. That work runs in the browser —
 * not on the VM.
 *
 * Default is Balanced: fine on typical 16 GB office laptops.
 * Ultra is for detail work on higher-RAM machines (e.g. 32–64 GB).
 */

export const RENDER_QUALITY_KEY = 'ga-dashboard-render-quality';

/** First paint / interim zoom layer — cheap so pan/zoom never blanks. */
export const PDF_PREVIEW_DPR = 1.25;

/** Settle delay (ms) after wheel/button zoom before starting a sharp re-raster. */
export const PDF_ZOOM_SETTLE_MS = 220;

/** @type {{ id: string, label: string, hint: string, maxDpr: number, baseWidth: number }[]} */
export const RENDER_QUALITY_OPTIONS = [
  {
    id: 'fast',
    label: '⚡ Fast',
    hint: 'Quick pan/zoom — softer when zoomed in. Best on lighter PCs.',
    maxDpr: 1.5,
    baseWidth: 1200,
  },
  {
    id: 'balanced',
    label: '⚖ Balanced',
    hint: 'Default — sharp enough for reviews on ~16 GB machines.',
    maxDpr: 2.5,
    baseWidth: 1600,
  },
  {
    id: 'high',
    label: '✦ High',
    hint: 'Crisper zoom. Comfortable on 16–32 GB if the drawing is one page.',
    maxDpr: 4,
    baseWidth: 1600,
  },
  {
    id: 'ultra',
    label: '◆ Ultra',
    hint: 'Max detail for mark-up. Prefer 32 GB+; can slow older GPUs.',
    maxDpr: 6,
    baseWidth: 2000,
  },
];

const byId = Object.fromEntries(RENDER_QUALITY_OPTIONS.map((o) => [o.id, o]));

export function getRenderQualityId() {
  try {
    const saved = localStorage.getItem(RENDER_QUALITY_KEY);
    if (saved && byId[saved]) return saved;
  } catch { /* private mode */ }
  return 'balanced';
}

export function setRenderQualityId(id) {
  if (!byId[id]) return;
  try { localStorage.setItem(RENDER_QUALITY_KEY, id); } catch { /* private mode */ }
}

export function getRenderQuality(id = getRenderQualityId()) {
  return byId[id] || byId.balanced;
}
